import { FormProvider, useForm } from 'react-hook-form'
import { Stack, Box } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledMediaUpload,
  ControlledSelect,
  ControlledTextField,
} from '@/components/inputs'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { FormComponentProps, UserGroups } from '@/types'
import { useAuth } from '@/hooks'
import { schema } from './validationSchema'
import {
  OTHER_SPECIE_OPTION_VALUE,
  QUICKSCAN_INCLUDE_EXCLUDE_OPTIONS,
  QUICKSCAN_SPECIE_CATEGORY_OPTIONS,
  QUICKSCAN_SPECIE_OPTIONS,
} from '../fieldsOptions'

export const QuickScanForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')
  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      other_specie: '',
      specie_category: '',
      include_or_exclude: '',
      explanation: '',
      notes: '',
      photos: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="specie"
              label={t('observation-log.fieldLabels.specie')}
              options={QUICKSCAN_SPECIE_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch('specie') === OTHER_SPECIE_OPTION_VALUE && (
            <Box width="25%">
              <ControlledTextField
                control={methods.control}
                name="other_specie"
                placeholder={t('observation-log.fieldLabels.otherSpecie')}
                errorMessagePrefix=""
              />
            </Box>
          )}
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="specie_category"
              label={t('observation-log.fieldLabels.specieCategory')}
              options={QUICKSCAN_SPECIE_CATEGORY_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="include_or_exclude"
              label={t('observation-log.fieldLabels.includeOrExclude')}
              options={QUICKSCAN_INCLUDE_EXCLUDE_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
        </Stack>

        <ControlledTextField
          control={methods.control}
          name="explanation"
          label={t('observation-log.fieldLabels.explanation')}
          errorMessagePrefix=""
        />

        <ControlledTextField
          control={methods.control}
          name="notes"
          label={t('observation-log.fieldLabels.notes')}
          errorMessagePrefix=""
        />

        <ControlledMediaUpload
          control={methods.control}
          name="photos"
          label={t('observation-log.fieldLabels.photos')}
          disabled={isLoading}
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
